<template>
  <div class="px-6 px-lg-14 pt-6 pt-lg-10">
    <div class="mx-0 pb-6 pb-lg-10 row align-items-end">
      <div class="text-h4 text-uppercase" style="display: inline-block;">{{ $t("invoice_management") }}</div>
    </div>

    <div class="row mx-0 mb-4">
      <div class="col">
        <div class="d-flex align-items-center">
          <v-btn
            icon
            :disabled="currentMonthOffset <= -maxMonthsBack"
            @click="navigateMonth(-1)"
            class="mr-2"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <v-menu
            ref="dateMenu"
            v-model="dateMenuOpen"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                class="rounded-0"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left>mdi-calendar</v-icon>
                {{ currentMonthDisplay }}
              </v-btn>
            </template>
            <v-date-picker
              v-model="selectedDate"
              @input="onDateSelected"
              type="month"
              :max="currentMonth"
              locale="de-DE"
              color="primary"
            ></v-date-picker>
          </v-menu>

          <v-btn
            icon
            :disabled="currentMonthOffset >= 0"
            @click="navigateMonth(1)"
            class="ml-2"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="col text-right">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              outlined
              class="rounded-0"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left>mdi-download</v-icon>
              {{ $t("download_year") }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="year in availableYears"
              :key="year"
              @click="downloadYearlyInvoices(year)"
            >
              <v-list-item-title>{{ year }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div class="page-content">
      <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

      <div v-if="!loading && invoices.length === 0" class="text-center py-8 text--secondary">
        <v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
        <div class="mt-2">{{ $t("no_invoices_for_month") }}</div>
      </div>

      <div v-if="!loading && invoices.length > 0">
        <v-card
          v-for="(invoice, index) in invoices"
          :key="invoice.id"
          class="invoice-card mb-3 rounded-0"
          outlined
        >
          <v-card-text class="pa-4">
            <div class="d-flex justify-space-between align-items-center">
              <div class="invoice-info">
                <div class="invoice-title">{{ formatInvoiceText(invoice.text) }}</div>
                <div class="invoice-meta">
                  <span class="invoice-date">{{ formatDate(invoice.createdAt) }}</span>
                  <span class="mx-2">•</span>
                  <span class="invoice-amount">{{ formatAmount(invoice.grossSumCents) }}</span>
                </div>
              </div>
              <div>
                <v-btn
                  icon
                  color="primary"
                  @click="downloadInvoice(invoice)"
                  :loading="invoice.downloading"
                  :title="$t('download_invoice')"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card-text>
          <v-divider v-if="index < invoices.length - 1"></v-divider>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Invoices",

  data() {
    return {
      invoices: [],
      loading: false,
      currentMonthOffset: 0,
      maxMonthsBack: 60, // 5 years
      dateMenuOpen: false,
      selectedDate: null,
      customer: null,
    };
  },

  computed: {
    currentMonthDisplay() {
      const date = this.getOffsetDate();
      return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    },
    currentMonth() {
      return new Date().toISOString().substr(0, 7);
    },
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // Show last 5 years
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years;
    }
  },

  mounted() {
    this.fetchCustomer();
  },

  methods: {
    async fetchCustomer() {
      try {
        const response = await this.$axios.get("/api/me");
        this.customer = response.data.customer;
        if (this.customer) {
          this.fetchInvoices();
        } else {
          this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t("no_customer_found"));
        }
      } catch (error) {
        this.onError(error);
      }
    },

    getOffsetDate() {
      const date = new Date();
      date.setMonth(date.getMonth() + this.currentMonthOffset);
      return date;
    },

    navigateMonth(direction) {
      const newOffset = this.currentMonthOffset + direction;
      if (newOffset <= 0 && newOffset >= -this.maxMonthsBack) {
        this.currentMonthOffset = newOffset;
        this.fetchInvoices();
      }
    },

    onDateSelected(date) {
      this.dateMenuOpen = false;
      // date is in format "YYYY-MM"
      const selected = new Date(date + "-01");
      const now = new Date();
      now.setDate(1); // Set to first of month for accurate comparison

      const monthsDiff = (now.getFullYear() - selected.getFullYear()) * 12 +
                         (now.getMonth() - selected.getMonth());

      this.currentMonthOffset = -monthsDiff;
      this.fetchInvoices();
    },

    async fetchInvoices() {
      if (!this.customer) return;

      this.loading = true;
      const date = this.getOffsetDate();
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // JavaScript months are 0-indexed

      try {
        const response = await this.$axios.get(
          `/api/payment/customer/${this.customer.id}/receipts`,
          {
            params: {
              year: year,
              month: month,
              page: 0,
              size: 100
            }
          }
        );

        // Filter invoices by the selected month
        this.invoices = response.data.filter(invoice => {
          const invoiceDate = new Date(invoice.createdAt);
          return invoiceDate.getFullYear() === year &&
                 invoiceDate.getMonth() === month - 1;
        }).map(invoice => ({
          ...invoice,
          downloading: false
        }));
      } catch (error) {
        this.onError(error);
      } finally {
        this.loading = false;
      }
    },

    async downloadInvoice(invoice) {
      invoice.downloading = true;
      try {
        const response = await this.$axios.get(
          `/api/payment/receipts/${invoice.id}/download`,
          {
            responseType: 'blob'
          }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice_${invoice.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$noty.success('<i class="fas fa-check mr-3"></i>' + this.$t("invoice_downloaded"));
      } catch (error) {
        this.onError(error);
      } finally {
        invoice.downloading = false;
      }
    },

    async downloadYearlyInvoices(year) {
      if (!this.customer) return;

      try {
        const response = await this.$axios.get(
          `/api/payment/customer/${this.customer.id}/receipts/download-year/${year}`,
          {
            responseType: 'blob'
          }
        );

        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoices_${year}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$noty.success('<i class="fas fa-check mr-3"></i>' + this.$t("yearly_invoices_downloaded"));
      } catch (error) {
        // Handle blob response errors (e.g., 404 when no invoices found)
        if (error.response && error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            if (error.response.status === 404) {
              this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t("no_invoices_for_year"));
              return;
            }
          } catch (e) {
            // If parsing fails, fall through to default error handling
          }
        }
        this.onError(error);
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },

    formatAmount(cents) {
      const euros = cents / 100;
      return euros.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR"
      });
    },

    formatInvoiceText(text) {
      if (!text) return this.$t("invoice");
      
      // Handle "Subscription (monthly)" or "Subscription (yearly)" format
      const subscriptionMatch = text.match(/^Subscription\s*\((\w+)\)$/i);
      if (subscriptionMatch) {
        const plan = subscriptionMatch[1].toLowerCase();
        if (plan === 'monthly') {
          return this.$t("subscription") + " (" + this.$t("monthly") + ")";
        } else if (plan === 'yearly') {
          return this.$t("subscription") + " (" + this.$t("yearly") + ")";
        }
      }
      
      return text;
    },

    getStatusColor(status) {
      switch (status) {
        case 'COMPLETED':
          return 'success';
        case 'OPEN':
          return 'warning';
        case 'CANCELED':
          return 'error';
        default:
          return 'grey';
      }
    },

    onError(error) {
      if (error.response && error.response.data) {
        const message = error.response.data.message || error.response.data.error || error.response.data;
        this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t(message));
      } else {
        console.error(error);
        this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t("error_occurred"));
      }
    }
  }
};
</script>

<style scoped>
.page-content {
  margin-top: 20px;
}

.invoice-card {
  border-color: #e0e0e0;
  transition: box-shadow 0.2s;
}

.invoice-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.invoice-info {
  flex: 1;
}

.invoice-title {
  font-weight: 500;
  font-size: 1rem;
  color: #212121;
  margin-bottom: 4px;
}

.invoice-meta {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #757575;
}

.invoice-date {
  color: #0069AE;
}

.invoice-amount {
  font-weight: 500;
  color: #212121;
}
</style>
